import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { useMayOpen } from '@/features/auth/access'
import { useAuth } from '@/features/auth/store'
import {
  formatMoney,
  formatPct,
  getCustomers,
  getInventory,
  getProducts,
  getSalesSummary,
  getTargets,
  MOVEMENT_LABELS,
  type Channel,
} from '@/features/analytics/api'
import { dashboardFor, LAYOUTS } from '@/features/analytics/dashboards'
import { getCounterDay, formatMoney as formatTill } from '@/features/pos/api'
import {
  AttainmentBars,
  ChannelSplit,
  ChartCard,
  CategoryMix,
  CoverList,
  Kpi,
  PanelCard,
  ProductBars,
  RevenueTrend,
  WindowPicker,
} from '@/features/analytics/panels'

/**
 * The role dashboards (H2), and the home screen for anybody who can read analytics.
 *
 * One component, four layouts. What changes between a cashier and a finance manager is
 * which panels are on the page and how long a window they open on -- not what the figures
 * mean and not what they are allowed to see, which the API decides from their scope. See
 * `features/analytics/dashboards.ts` for why it is arranged that way.
 *
 * Every panel is its own query. A dashboard that waited for six endpoints before drawing
 * anything would be as slow as its slowest one, and the exit criterion for H2 is two
 * seconds; this way the revenue chart is on screen while the basket analysis is still
 * running, and one panel failing leaves the other five standing.
 */
export function RoleDashboard() {
  const user = useAuth((s) => s.user)
  const kind = dashboardFor(user?.roles.map((role) => role.slug) ?? [])
  const layout = LAYOUTS[kind]

  const [days, setDays] = useState(layout.defaultDays)
  const channel: Channel | '' = layout.posOnly ? 'pos' : ''
  const panels = layout.panels

  const sales = useQuery({
    queryKey: ['analytics', 'sales', days, channel],
    queryFn: () => getSalesSummary({ days, channel }),
    retry: false,
  })
  const targets = useQuery({
    queryKey: ['analytics', 'targets'],
    queryFn: getTargets,
    retry: false,
    enabled: panels.targets,
  })
  const products = useQuery({
    queryKey: ['analytics', 'products', days, 8],
    queryFn: () => getProducts({ days, limit: 8 }),
    retry: false,
    enabled: panels.products || panels.categories,
  })
  const customers = useQuery({
    queryKey: ['analytics', 'customers', days],
    queryFn: () => getCustomers({ days: Math.max(days, 90), limit: 10 }),
    retry: false,
    enabled: panels.customers,
  })
  const inventory = useQuery({
    queryKey: ['analytics', 'inventory', days],
    queryFn: () => getInventory({ days, limit: 6 }),
    retry: false,
    enabled: panels.inventory,
  })

  // The one figure on this page that is not a rollup. A shop is run on what the till has
  // taken since it opened, and the rollups cannot answer that: they are recomputed from
  // the outbox and their window is anchored to the last day the business traded, so on a
  // morning nothing has sold yet they read yesterday. Polled rather than pushed -- a
  // minute stale is close enough to run a counter on, and a socket per dashboard is not.
  const today = useQuery({
    queryKey: ['pos', 'today'],
    queryFn: () => getCounterDay(),
    retry: false,
    enabled: layout.posOnly,
    refetchInterval: 60_000,
  })

  const totals = sales.data?.totals
  const mayOpenSales = useMayOpen('/analytics/sales')

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{layout.title}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {layout.description}
            {layout.posOnly && ' Counter sales only.'}
          </p>
        </div>
        <WindowPicker days={days} onChange={setDays} asOf={sales.data?.as_of} />
      </header>

      {layout.posOnly && (
        <div className="grid gap-4 tablet:grid-cols-2 desktop:grid-cols-4">
          <Kpi
            label="Taken today"
            value={formatTill(today.data?.net_sales ?? null)}
            hint={
              today.data?.last_sale_at
                ? `last sale ${new Date(today.data.last_sale_at).toLocaleTimeString()}`
                : 'nothing rung up yet'
            }
            loading={today.isLoading}
          />
          <Kpi
            label="Receipts today"
            value={today.data ? String(today.data.transaction_count) : '—'}
            hint={`${today.data?.voided_count ?? 0} voided`}
            loading={today.isLoading}
          />
          <Kpi
            label="Average basket"
            value={formatTill(today.data?.average_basket ?? null)}
            hint="per receipt, tax included"
            loading={today.isLoading}
          />
          <Kpi
            label="Cash in the drawer"
            value={formatTill(today.data?.cash_takings ?? null)}
            hint={`${formatTill(today.data?.card_takings ?? null)} on cards`}
            loading={today.isLoading}
          />
        </div>
      )}

      <div className="grid gap-4 tablet:grid-cols-2 desktop:grid-cols-4">
        <Kpi
          label="Revenue"
          value={formatMoney(totals?.net ?? null)}
          delta={sales.data?.delta.net_pct ?? null}
          hint="vs previous period"
          loading={sales.isLoading}
        />
        <Kpi
          label="Margin"
          value={formatMoney(totals?.margin ?? null)}
          hint={`${formatPct(totals?.margin_pct)} of revenue`}
          loading={sales.isLoading}
        />
        <Kpi
          label={layout.posOnly ? 'Receipts' : 'Transactions'}
          value={totals ? String(totals.transactions) : '—'}
          hint={`${totals?.units ?? 0} units`}
          loading={sales.isLoading}
        />
        {panels.inventory ? (
          <Kpi
            label="Stock turnover"
            value={
              inventory.data?.turnover != null ? `${inventory.data.turnover.toFixed(2)}×` : '—'
            }
            hint={`${formatMoney(inventory.data?.inventory_value ?? null)} on the shelf`}
            loading={inventory.isLoading}
          />
        ) : (
          <Kpi
            label="Attainment"
            value={formatPct(targets.data?.attainment_pct)}
            hint={`month ${formatPct(targets.data?.month_elapsed_pct, 0)} elapsed`}
            loading={targets.isLoading}
          />
        )}
      </div>

      {panels.trend && (
        <ChartCard
          title="Revenue and margin"
          description={
            sales.data ? `${sales.data.period_start} to ${sales.data.period_end}` : undefined
          }
          isLoading={sales.isLoading}
          error={sales.error}
          isEmpty={!sales.data?.series.length}
          emptyMessage={`Nothing was sold in the last ${days} days.`}
          height={280}
          action={
            mayOpenSales ? (
              <Link
                to="/analytics/sales"
                className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-300"
              >
                Open sales analytics
              </Link>
            ) : undefined
          }
        >
          <RevenueTrend series={sales.data?.series ?? []} />
        </ChartCard>
      )}

      <div className="grid gap-4 desktop:grid-cols-2">
        {panels.channels && (
          <ChartCard
            title="Where the revenue came from"
            description="Order book against the till."
            isLoading={sales.isLoading}
            error={sales.error}
            isEmpty={!sales.data?.channels.length}
            emptyMessage="No sales in this window, through either channel."
          >
            <ChannelSplit channels={sales.data?.channels ?? []} />
          </ChartCard>
        )}

        {panels.targets && (
          <ChartCard
            title="Against target, month to date"
            description="The dashed line is how far through the month we are."
            isLoading={targets.isLoading}
            error={targets.error}
            isEmpty={!targets.data?.reps.length}
            emptyMessage="Nobody you can see carries a monthly sales target."
          >
            <AttainmentBars
              reps={targets.data?.reps ?? []}
              elapsedPct={targets.data?.month_elapsed_pct ?? 0}
            />
          </ChartCard>
        )}

        {panels.products && (
          <ChartCard
            title="Top sellers"
            description={`By revenue over ${days} days.`}
            isLoading={products.isLoading}
            error={products.error}
            isEmpty={!products.data?.top.length}
            emptyMessage="No product sold in this window."
          >
            <ProductBars rows={products.data?.top ?? []} />
          </ChartCard>
        )}

        {panels.categories && (
          <ChartCard
            title="Revenue by category"
            isLoading={products.isLoading}
            error={products.error}
            isEmpty={!products.data?.categories.length}
            emptyMessage="No category sold in this window."
          >
            <CategoryMix categories={products.data?.categories ?? []} />
          </ChartCard>
        )}

        {panels.inventory && (
          <PanelCard
            title="Running out next"
            description="On-hand units over daily demand in this window."
            isLoading={inventory.isLoading}
            error={inventory.error}
            isEmpty={!inventory.data?.at_risk.length}
            emptyMessage="Nothing you stock is close to running out."
          >
            <CoverList rows={inventory.data?.at_risk ?? []} tone="risk" />
          </PanelCard>
        )}

        {panels.customers && (
          <PanelCard
            title="Customers to move a tier"
            description="Where spend and the tier on file disagree."
            isLoading={customers.isLoading}
            error={customers.error}
            isEmpty={!customers.data?.movements.length}
            emptyMessage="Every customer is on the tier their spend puts them on."
          >
            <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {(customers.data?.movements ?? []).slice(0, 6).map((row) => (
                <li key={row.customer_id} className="flex items-center justify-between gap-3 py-2">
                  <span className="min-w-0">
                    <span className="block truncate text-slate-700 dark:text-slate-300">
                      {row.customer_name ?? row.customer_id.slice(0, 8)}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {row.segment} · {formatMoney(row.monetary)}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs font-medium">
                    {MOVEMENT_LABELS[row.movement]} {row.tier} → {row.suggested_tier}
                  </span>
                </li>
              ))}
            </ul>
          </PanelCard>
        )}
      </div>
    </div>
  )
}
